import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { updateVision, deleteVision } from '../../actions/planningAction';
import './PlanningSettings.css';

export default function PlanningSettings({ vision, visionId, theme, onUpdated }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [title, setTitle] = useState(vision?.title || '');
  const [timeframe, setTimeframe] = useState(vision?.timeframe || 1);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const dirty = title.trim() !== (vision?.title || '') || timeframe !== (vision?.timeframe || 1);

  const save = async () => {
    if (!title.trim() || saving) return;
    setSaving(true);
    try {
      await dispatch(updateVision(visionId, { title: title.trim(), timeframe }));
      onUpdated?.();
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1800);
    } catch (_) {}
    setSaving(false);
  };

  const remove = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await dispatch(deleteVision(visionId));
      navigate('/dashboard/planning', { replace: true });
    } catch (_) {
      setDeleting(false);
    }
  };

  return (
    <div className={`ps-wrap${theme === 'light' ? ' ps-wrap--light' : ''}`}>
      <div className="ps-inner">
        <div className="ps-header">
          <h1 className="ps-heading">Vision settings</h1>
          <p className="ps-sub">Manage this vision: its name, horizon, and removal.</p>
        </div>

        {/* General */}
        <section className="ps-card">
          <div className="ps-card-label">General</div>

          <div className="ps-field">
            <label className="ps-field-label">Vision name</label>
            <input
              className="ps-input"
              value={title}
              maxLength={200}
              onChange={e => setTitle(e.target.value)}
              placeholder="What are you building?"
            />
          </div>

          <div className="ps-field">
            <label className="ps-field-label">Horizon</label>
            <div className="ps-yr-row">
              {[1, 2, 3, 4, 5].map(y => (
                <button
                  key={y}
                  className={`ps-yr-btn${timeframe === y ? ' ps-yr-btn--on' : ''}`}
                  onClick={() => setTimeframe(y)}
                >
                  {y}y
                </button>
              ))}
            </div>
            <p className="ps-hint">
              {timeframe === 5 ? 'PROJECT-5: 5-year maximum ambition horizon.' : `${timeframe}-year plan`}
            </p>
          </div>

          <div className="ps-actions">
            <button className="ps-save-btn" onClick={save} disabled={!dirty || !title.trim() || saving}>
              {saving ? 'Saving…' : savedFlash ? '✓ Saved' : 'Save changes'}
            </button>
          </div>
        </section>

        {/* Danger zone */}
        <section className="ps-card ps-card--danger">
          <div className="ps-card-label ps-card-label--danger">Danger zone</div>
          <div className="ps-danger-row">
            <div className="ps-danger-text">
              <p className="ps-danger-title">Delete this vision</p>
              <p className="ps-danger-desc">
                Permanently removes this vision and everything inside it: all milestones, actions, documents, habits, todos, and journal. This cannot be undone.
              </p>
            </div>
            <button className="ps-delete-btn" onClick={() => { setShowDelete(true); setConfirmText(''); }}>
              Delete vision
            </button>
          </div>
        </section>
      </div>

      {showDelete && (
        <div className="ps-modal-overlay" onMouseDown={() => !deleting && setShowDelete(false)}>
          <div className="ps-modal" onMouseDown={e => e.stopPropagation()}>
            <h3 className="ps-modal-title">Delete "{vision?.title || 'this vision'}"?</h3>
            <p className="ps-modal-text">
              This wipes the entire vision and all its contents. There's no undo. Type <strong>DELETE</strong> to confirm.
            </p>
            <input
              className="ps-modal-input"
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              placeholder="DELETE"
              autoFocus
            />
            <div className="ps-modal-actions">
              <button className="ps-modal-cancel" onClick={() => setShowDelete(false)} disabled={deleting}>Cancel</button>
              <button
                className="ps-modal-go"
                onClick={remove}
                disabled={confirmText.trim().toUpperCase() !== 'DELETE' || deleting}
              >
                {deleting ? 'Deleting…' : 'Delete forever'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
